import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { fetchWallet } from '../../store/slices/walletSlice';
import { fetchUserProfile } from '../../store/slices/authSlice';
import { reportService } from '../../services/api/reportService';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n: number | string) =>
  new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP', minimumFractionDigits: 2 })
    .format(Number(n));

const localISO = (d = new Date()) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const toBackendDate = (iso: string) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

const getPeriodRange = (period: 'day' | 'week' | 'month') => {
  const today = new Date();
  const to = localISO(today);
  if (period === 'day') return { from: to, to };
  if (period === 'week') {
    const d = new Date(today); d.setDate(d.getDate() - 6);
    return { from: localISO(d), to };
  }
  const d = new Date(today); d.setDate(d.getDate() - 29);
  return { from: localISO(d), to };
};

const TYPE_LABEL: Record<number, string> = { 1: 'Recarga', 2: 'Paquete', 3: 'Factura', 4: 'Seguro' };
const TYPE_COLOR: Record<number, string> = {
  1: '#2563eb', 2: '#06b6d4', 3: '#10b981', 4: '#f59e0b',
};
const STATUS_LABEL: Record<number, string> = {
  0: 'Completada', 1: 'Pendiente', 2: 'Fallida', 3: 'Reversada',
};
const STATUS_COLOR: Record<number, string> = {
  0: '#10b981', 1: '#f59e0b', 2: '#ef4444', 3: '#94a3b8',
};
const PERIOD_LABELS = { day: 'Hoy', week: 'Últimos 7 días', month: 'Últimos 30 días' };

// ─── KPI Card ─────────────────────────────────────────────────────────────────

const KpiCard: React.FC<{
  title: string; value: string; sub: string;
  icon: React.ReactNode; gradient: string; glow: string;
  loading?: boolean;
}> = ({ title, value, sub, icon, gradient, glow, loading }) => (
  <div className="rounded-2xl p-5 text-white relative overflow-hidden"
    style={{ background: gradient, boxShadow: `0 8px 24px ${glow}` }}>
    <div className="absolute -right-4 -top-4 w-24 h-24 rounded-full opacity-20" style={{ background: 'rgba(255,255,255,0.3)' }} />
    <div className="absolute -right-2 bottom-2 w-16 h-16 rounded-full opacity-10" style={{ background: 'rgba(255,255,255,0.3)' }} />
    <div className="relative z-10">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold opacity-85 uppercase tracking-wide">{title}</p>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.2)' }}>
          {icon}
        </div>
      </div>
      {loading
        ? <div className="h-8 w-28 rounded-lg animate-pulse mb-1" style={{ background: 'rgba(255,255,255,0.2)' }} />
        : <p className="text-xl font-black tracking-tight mb-1 truncate" title={value}>{value}</p>
      }
      <p className="text-xs opacity-70 truncate">{sub}</p>
    </div>
  </div>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

// Cache simple por período para no repetir llamadas innecesarias
const cache: Record<string, { rows: any[]; totals: any; ts: number }> = {};
const CACHE_TTL = 2 * 60 * 1000; // 2 minutos

const DashboardPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user }   = useAppSelector((s) => s.auth as any);
  const { wallets = [], selectedWalletId, wallet } = useAppSelector((s) => s.wallet as any);
  // lastMovements eliminado — reutilizamos dashRows (sin llamada extra)

  // ── Estado LOCAL del dashboard ────────────────────────────────────────────────
  const [loading,        setLoading]        = useState(false);
  const [dashRows,       setDashRows]       = useState<any[]>([]);
  const [dashTotals,     setDashTotals]     = useState<any>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<'day' | 'week' | 'month'>('week');
  const abortRef = useRef<AbortController | null>(null);

  // ── Carga de datos — 4 llamadas paralelas, sin type:5 redundante ─────────────
  const loadData = useCallback(async (period: 'day' | 'week' | 'month', force = false) => {
    const { from, to } = getPeriodRange(period);
    const cacheKey = `${period}-${from}-${to}`;

    // Devolver caché si está vigente y no es forzado
    if (!force && cache[cacheKey] && Date.now() - cache[cacheKey].ts < CACHE_TTL) {
      setDashRows(cache[cacheKey].rows);
      setDashTotals(cache[cacheKey].totals);
      return;
    }

    // Cancelar carga anterior si aún está en vuelo
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    setLoading(true);
    try {
      const fromBE = toBackendDate(from);
      const toBE   = toBackendDate(to);

      // 4 llamadas en paralelo — eliminamos type:5 y calculamos totales sumando
      const [res1, res2, res3, res4] = await Promise.all([
        reportService.getReportData(fromBE, toBE, 1),
        reportService.getReportData(fromBE, toBE, 2),
        reportService.getReportData(fromBE, toBE, 3),
        reportService.getReportData(fromBE, toBE, 4),
      ]);

      const parseNum = (v: any) => Number(String(v ?? '0').replace(/,/g, '')) || 0;

      // Combinar totales sumando los 4 resultados
      const combinedTotals = {
        totals_solds:     0,
        total_reverse:    0,
        total_error:      0,
        total_amount:     '0',
        total_commission: '0',
      };
      let sumAmount = 0;
      let sumComm   = 0;
      for (const res of [res1, res2, res3, res4]) {
        if (res.success && res.data) {
          combinedTotals.totals_solds  += Number(res.data.totals_solds  ?? 0);
          combinedTotals.total_reverse += Number(res.data.total_reverse ?? 0);
          combinedTotals.total_error   += Number(res.data.total_error   ?? 0);
          sumAmount += parseNum(res.data.total_amount);
          sumComm   += parseNum(res.data.total_commission);
        }
      }
      combinedTotals.total_amount     = sumAmount.toFixed(2);
      combinedTotals.total_commission = sumComm.toFixed(2);

      // Combinar filas de los 4 tipos
      const allRows: any[] = [];
      for (const res of [res1, res2, res3, res4]) {
        if (res.success && res.data?.data && Array.isArray(res.data.data)) {
          allRows.push(...res.data.data);
        }
      }

      // Guardar en caché
      cache[cacheKey] = { rows: allRows, totals: combinedTotals, ts: Date.now() };

      setDashRows(allRows);
      setDashTotals(combinedTotals);
    } catch (e: any) {
      if (e?.name !== 'AbortError') console.error('[Dashboard] error al cargar:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(selectedPeriod); }, [selectedPeriod, loadData]);

  // Cargar wallets y perfil solo si no están en Redux
  useEffect(() => {
    if (!wallets || wallets.length === 0) dispatch(fetchWallet());
  }, [dispatch, wallets]);

  useEffect(() => {
    if (!user?.first_name) dispatch(fetchUserProfile());
  }, [dispatch, user]);

  // ── Datos derivados ───────────────────────────────────────────────────────────
  const firstName = user?.first_name || user?.name?.split(' ')[0] || 'Usuario';
  const selectedWallet = wallets.find((w: any) => String(w.id) === String(selectedWalletId));
  const walletBalance  = Number(selectedWallet?.amount ?? wallet?.balance ?? 0);

  const parseAmt = (v: any) => Number(String(v ?? 0).replace(/,/g, '')) || 0;

  // Ventas por día
  const salesByDay: Record<string, { amount: number; count: number }> = {};
  dashRows.forEach((m: any) => {
    const raw = m.date || m.created_at || '';
    const key = String(raw).substring(0, 10);
    if (!key || key.length < 8) return;
    if (!salesByDay[key]) salesByDay[key] = { amount: 0, count: 0 };
    salesByDay[key].amount += parseAmt(m.amount);
    salesByDay[key].count  += 1;
  });
  const dailyBars = Object.entries(salesByDay).sort(([a], [b]) => a.localeCompare(b)).slice(-14);
  const maxDayAmount = Math.max(...dailyBars.map(([, v]) => v.amount), 1);

  // Ventas por tipo — totales del API (precisos) + conteo de filas
  const byType: Record<number, { amount: number; count: number }> = {};
  dashRows.forEach((m: any) => {
    const t = Number(m.type);
    if (!byType[t]) byType[t] = { amount: 0, count: 0 };
    byType[t].amount += parseAmt(m.amount);
    byType[t].count  += 1;
  });
  const categoryBars = Object.entries(byType)
    .map(([type, v]) => ({ type: Number(type), ...v }))
    .sort((a, b) => b.amount - a.amount);
  const totalCatAmount = categoryBars.reduce((s, c) => s + c.amount, 1);

  // Top proveedores
  const byProvider: Record<string, { amount: number; count: number }> = {};
  dashRows.forEach((m: any) => {
    const op = m.operator || '—';
    if (!byProvider[op]) byProvider[op] = { amount: 0, count: 0 };
    byProvider[op].amount += parseAmt(m.amount);
    byProvider[op].count  += 1;
  });
  const providerRows = Object.entries(byProvider)
    .map(([name, v]) => ({ name, ...v }))
    .sort((a, b) => b.amount - a.amount);
  const totalPrvAmount = providerRows.reduce((s, p) => s + p.amount, 1);

  // Últimas transacciones — reutiliza las filas ya cargadas (sin llamada extra)
  const recentMovements = [...dashRows]
    .sort((a: any, b: any) =>
      new Date(b.date || b.created_at || 0).getTime() -
      new Date(a.date || a.created_at || 0).getTime()
    )
    .slice(0, 5);

  // Totales del período
  const totalAmount  = parseAmt(dashTotals?.total_amount);
  const totalComm    = parseAmt(dashTotals?.total_commission);
  const totalSolds   = dashTotals?.totals_solds  ?? 0;
  const totalReverse = dashTotals?.total_reverse ?? 0;
  const totalError   = dashTotals?.total_error   ?? 0;

  return (
    <div className="p-6 max-w-7xl mx-auto">

      {/* Header */}
      <div className="mb-6 animate-fadeIn">
        <h1 className="text-2xl font-black text-slate-800">
          Hola, <span style={{ color: '#2563eb' }}>{firstName}</span> 👋
        </h1>
        <p className="text-slate-500 mt-1">Resumen de tus operaciones</p>
      </div>

      {/* Period selector */}
      <div className="flex gap-2 mb-7 animate-fadeIn">
        {(['day', 'week', 'month'] as const).map(p => (
          <button key={p} onClick={() => setSelectedPeriod(p)}
            className="px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200"
            style={selectedPeriod === p
              ? { background: '#2563eb', color: '#fff', boxShadow: '0 4px 12px rgba(37,99,235,0.35)' }
              : { background: '#fff', color: '#64748b', border: '1px solid #e2e8f0' }}>
            {PERIOD_LABELS[p]}
          </button>
        ))}
        <button onClick={() => loadData(selectedPeriod, true)}
          className="ml-auto px-3 py-2 rounded-xl transition-all hover:bg-slate-100"
          style={{ color: '#64748b', border: '1px solid #e2e8f0', background: '#fff' }}
          title="Actualizar">
          <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
          </svg>
        </button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8 animate-fadeIn">
        <KpiCard
          title="Ventas Totales" loading={loading}
          value={fmt(totalAmount)}
          sub={`${totalSolds} completadas`}
          gradient="linear-gradient(135deg, #1e40af 0%, #2563eb 100%)"
          glow="rgba(37,99,235,0.3)"
          icon={<svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>}
        />
        <KpiCard
          title="Comisiones" loading={loading}
          value={fmt(totalComm)}
          sub="Comisiones generadas"
          gradient="linear-gradient(135deg, #0e7490 0%, #06b6d4 100%)"
          glow="rgba(6,182,212,0.3)"
          icon={<svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>}
        />
        <KpiCard
          title="Completadas" loading={loading}
          value={String(totalSolds)}
          sub={`${totalReverse} reversadas · ${totalError} fallidas`}
          gradient="linear-gradient(135deg, #065f46 0%, #10b981 100%)"
          glow="rgba(16,185,129,0.3)"
          icon={<svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>}
        />
        <KpiCard
          title="Balance Wallet" loading={false}
          value={fmt(walletBalance)}
          sub={selectedWallet?.name ? selectedWallet.name : 'Saldo disponible'}
          gradient="linear-gradient(135deg, #6d28d9 0%, #8b5cf6 100%)"
          glow="rgba(139,92,246,0.3)"
          icon={<svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/></svg>}
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8 animate-fadeIn">

        {/* Ventas por día */}
        <div className="bg-white rounded-2xl p-6 shadow-card">
          <h3 className="text-base font-bold text-slate-700 mb-5">Ventas por Día</h3>
          {loading ? (
            <div className="flex items-end gap-2" style={{ height: 160 }}>
              {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className="flex-1 rounded-t-lg animate-pulse" style={{ height: 40 + i * 15, background: '#e2e8f0' }} />
              ))}
            </div>
          ) : dailyBars.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-slate-400 text-sm">Sin datos para el período</div>
          ) : (
            <div className="flex items-end gap-1.5" style={{ height: 160 }}>
              {dailyBars.map(([date, v], i) => {
                // Usamos px fijos — los % no funcionan cuando el padre no tiene altura CSS explícita
                const BAR_MAX = 140; // px máximo de barra
                const barPx  = Math.max(Math.round((v.amount / maxDayAmount) * BAR_MAX), 6);
                const day    = date.split('-')[2];
                return (
                  <div key={i} className="flex flex-col items-center flex-1 gap-1 group relative" style={{ height: '100%', justifyContent: 'flex-end' }}>
                    {/* Tooltip */}
                    <div className="absolute left-1/2 -translate-x-1/2 hidden group-hover:flex flex-col items-center z-10 pointer-events-none"
                      style={{ bottom: 22 }}>
                      <div className="bg-slate-800 text-white text-xs rounded-lg px-2 py-1 whitespace-nowrap shadow-lg text-center">
                        {fmt(v.amount)}<br /><span className="opacity-75">{v.count} ops</span>
                      </div>
                      <div className="w-2 h-2 bg-slate-800 rotate-45 -mt-1" />
                    </div>
                    {/* Barra */}
                    <div className="w-full rounded-t-lg transition-all duration-500 cursor-pointer hover:opacity-75"
                      style={{ height: barPx, background: 'linear-gradient(180deg, #60a5fa, #1e40af)', flexShrink: 0 }} />
                    {/* Día */}
                    <span className="text-xs text-slate-400 mt-1" style={{ flexShrink: 0 }}>{day}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Ventas por tipo */}
        <div className="bg-white rounded-2xl p-6 shadow-card">
          <h3 className="text-base font-bold text-slate-700 mb-5">Ventas por Tipo</h3>
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i}>
                  <div className="h-4 w-1/2 rounded animate-pulse mb-2" style={{ background: '#e2e8f0' }} />
                  <div className="h-3 rounded-full animate-pulse" style={{ background: '#e2e8f0' }} />
                </div>
              ))}
            </div>
          ) : categoryBars.length === 0 ? (
            <div className="flex items-center justify-center h-48 text-slate-400 text-sm">Sin datos para el período</div>
          ) : (
            <div className="space-y-4">
              {categoryBars.map((cat) => {
                const pct = (cat.amount / totalCatAmount) * 100;
                const color = TYPE_COLOR[cat.type] || '#94a3b8';
                return (
                  <div key={cat.type}>
                    <div className="flex justify-between text-sm mb-1.5">
                      <span className="font-semibold text-slate-700">{TYPE_LABEL[cat.type] || `Tipo ${cat.type}`}</span>
                      <span className="font-bold" style={{ color }}>{fmt(cat.amount)}</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                      <div className="h-3 rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: color }} />
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">{cat.count} transacciones · {pct.toFixed(1)}%</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fadeIn">

        {/* Top proveedores */}
        <div className="bg-white rounded-2xl shadow-card overflow-hidden flex flex-col">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-base font-bold text-slate-700">Proveedores</h3>
            {providerRows.length > 0 && (
              <span className="text-xs font-semibold px-2 py-1 rounded-full" style={{ background: '#eff6ff', color: '#2563eb' }}>
                {providerRows.length} {providerRows.length === 1 ? 'proveedor' : 'proveedores'}
              </span>
            )}
          </div>
          {loading ? (
            <div className="p-6 space-y-3">
              {[1, 2, 3].map(i => <div key={i} className="h-10 rounded-xl animate-pulse" style={{ background: '#f1f5f9' }} />)}
            </div>
          ) : providerRows.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-slate-400 text-sm">Sin datos para el período</div>
          ) : (
            <div className="overflow-auto" style={{ maxHeight: 360 }}>
              <table className="min-w-full">
                <thead className="sticky top-0 z-10">
                  <tr style={{ background: '#f8fafc' }}>
                    {['Proveedor', 'Ops', 'Total', '%'].map(h => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {providerRows.map((p, i) => {
                    const pct = (p.amount / totalPrvAmount) * 100;
                    return (
                      <tr key={i} className="border-t border-slate-50 hover:bg-slate-50 transition-colors">
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                              style={{ background: 'linear-gradient(135deg, #2563eb, #06b6d4)' }}>
                              {p.name.charAt(0)}
                            </div>
                            <span className="font-semibold text-slate-800 text-sm">{p.name}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-slate-600 text-sm">{p.count}</td>
                        <td className="px-5 py-3 font-bold text-slate-800 text-sm whitespace-nowrap">{fmt(p.amount)}</td>
                        <td className="px-5 py-3 min-w-[100px]">
                          <div className="flex items-center gap-1.5">
                            <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                              <div className="h-2 rounded-full" style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #2563eb, #06b6d4)' }} />
                            </div>
                            <span className="text-xs font-bold text-slate-400 w-9 text-right">{pct.toFixed(0)}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Últimas transacciones */}
        <div className="bg-white rounded-2xl shadow-card overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100">
            <h3 className="text-base font-bold text-slate-700">Últimas Transacciones</h3>
          </div>
          {recentMovements.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-slate-400 text-sm">Sin movimientos recientes</div>
          ) : (
            <div className="divide-y divide-slate-50">
              {recentMovements.map((m: any, i: number) => {
                const typeNum  = Number(m.type);
                const color    = TYPE_COLOR[typeNum]    || '#94a3b8';
                const scColor  = STATUS_COLOR[m.status] || '#94a3b8';
                return (
                  <div key={m.xid || m.id || i} className="flex items-center gap-3 px-6 py-3 hover:bg-slate-50 transition-colors">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold"
                      style={{ background: color }}>
                      {(TYPE_LABEL[typeNum] || '?').charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">
                        {m.operator || '—'} · {m.phone_number || m.name || '—'}
                      </p>
                      <p className="text-xs text-slate-400 truncate">
                        {TYPE_LABEL[typeNum] || `Tipo ${m.type}`} · {m.xid || ''}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold text-slate-800">{fmt(Number(m.amount || 0))}</p>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
                        style={{ background: `${scColor}18`, color: scColor }}>
                        {STATUS_LABEL[m.status] ?? 'Estado ' + m.status}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
